// src/components/Reports/ReportViewer.tsx

import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { MoreVertical } from 'lucide-react';
import DashboardReportService from '@services/DashboardReportService';
import type { ReportWidget, ReportFilters, LayerData } from '@services/DashboardReportService';
import type { ReportViewerProps } from './types';
import { StatisticsWidget } from './widgets/StatisticsWidget';
import { DetailsWidget } from './widgets/DetailsWidget';
import { BurndownChartWidget } from './widgets/BurndownChartWidget';
import { KPIChartWidget } from './widgets/KPIChartWidget';
import { TrendChartWidget } from './widgets/TrendChartWidget';
import './ReportViewer.css';

// Widget type IDs
// 3 = Statistics, 4 = Details, 5 = BurnDownChartFinished, 6 = KPIChart, 7 = TrendChart
const WIDGET_TYPE = {
  Statistics: 3,
  Details: 4,
  BurnDownChartFinished: 5,
  KPIChart: 6,
  TrendChart: 7,
} as const;

const MAX_WIDGETS_PER_TYPE = 3;

function countByName(widgets: ReportWidget[]): Record<string, number>
{
  const counts: Record<string, number> = {};
  for (const w of widgets)
  {
    counts[w.Name] = (counts[w.Name] ?? 0) + 1;
  }
  return counts;
}

function buildShowMenuItem(counts: Record<string, number>): Record<string, boolean>
{
  return {
    BurnDownChartFinished: (counts['BurnDownChartFinished'] ?? 0) < MAX_WIDGETS_PER_TYPE,
    Statistics: (counts['Statistics'] ?? 0) < MAX_WIDGETS_PER_TYPE,
    Details: (counts['Details'] ?? 0) < MAX_WIDGETS_PER_TYPE,
    KPIChart: (counts['KPIChart'] ?? 0) < MAX_WIDGETS_PER_TYPE,
    TrendChart: (counts['TrendChart'] ?? 0) < MAX_WIDGETS_PER_TYPE,
  };
}

export const ReportViewer: React.FC<ReportViewerProps> = ({
  dashboard,
  projectId,
  onClose,
  onReportCreated,
  onReportDeleted,
}) =>
{
  const [widgets, setWidgets] = useState<ReportWidget[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reportId, setReportId] = useState<number>(dashboard.ReportId ?? 0);
  const [showMenu, setShowMenu] = useState(false);
  const [showMenuItem, setShowMenuItem] = useState<Record<string, boolean>>({
    BurnDownChartFinished: true,
    Statistics: true,
    Details: true,
    KPIChart: true,
    TrendChart: true,
  });
  const [lastUpdate, setLastUpdate] = useState<string>('');
  const [layers, setLayers] = useState<LayerData[]>([]);

  const menuRef = useRef<HTMLDivElement>(null);

  // Draggable modal position
  const [position, setPosition] = useState<{ x: number; y: number }>(() => ({
    x: Math.max(0, (window.innerWidth - 750) / 2),
    y: Math.max(0, window.innerHeight * 0.05),
  }));
  const isDragging = useRef(false);
  const dragOffset = useRef({ x: 0, y: 0 });

  const handleHeaderMouseDown = (e: React.MouseEvent) =>
  {
    isDragging.current = true;
    dragOffset.current = { x: e.clientX - position.x, y: e.clientY - position.y };
    e.preventDefault();
  };

  useEffect(() =>
  {
    const handleMouseMove = (e: MouseEvent) =>
    {
      if (!isDragging.current) return;
      setPosition({ x: e.clientX - dragOffset.current.x, y: e.clientY - dragOffset.current.y });
    };
    const handleMouseUp = () =>
    {
      isDragging.current = false;
    };
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () =>
    {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  const defaultFilters = useMemo<ReportFilters>(
    () => ({
      CustomFields: [],
      PageNumber: 1,
      PageSize: 100,
      DashboardId: null,
      SearchText: '',
      Project: String(projectId || ''),
    }),
    [projectId],
  );

  // Update lastUpdate timestamp
  const touchLastUpdate = () =>
  {
    const now = new Date();
    setLastUpdate(`${now.toLocaleDateString()} ${now.toLocaleTimeString()}`);
  };

  // Process a list of widgets: deduplicate excess beyond MAX_WIDGETS_PER_TYPE
  const processWidgets = useCallback(
    async (raw: ReportWidget[], currentReportId: number): Promise<ReportWidget[]> =>
    {
      const countsByName: Record<string, number> = {};
      const keep: ReportWidget[] = [];
      const toDelete: ReportWidget[] = [];

      for (const w of raw)
      {
        countsByName[w.Name] = (countsByName[w.Name] ?? 0) + 1;
        if (countsByName[w.Name]! <= MAX_WIDGETS_PER_TYPE)
        {
          keep.push(w);
        }
        else
        {
          toDelete.push(w);
        }
      }

      // Delete excess widgets in background
      for (const w of toDelete)
      {
        DashboardReportService.deleteWidget(currentReportId, w.WidgetId).catch(() => {});
      }

      return keep;
    },
    [],
  );

  // Load layers
  useEffect(() =>
  {
    DashboardReportService.getPersonLayers()
      .then(setLayers)
      .catch(() => {});
  }, []);

  // Close on Escape
  useEffect(() =>
  {
    const handleKeyDown = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  // Close menu on outside click
  useEffect(() =>
  {
    const handleClick = (e: MouseEvent) =>
    {
      if (menuRef.current && !menuRef.current.contains(e.target as Node))
      {
        setShowMenu(false);
      }
    };
    if (showMenu) document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showMenu]);

  // Initial load
  const initialize = useCallback(async () =>
  {
    try
    {
      setLoading(true);
      setError(null);

      let currentReportId = dashboard.ReportId ?? 0;
      let rawWidgets: ReportWidget[];

      if (currentReportId > 0)
      {
        rawWidgets = await DashboardReportService.getWidgets(currentReportId);
      }
      else
      {
        rawWidgets = await DashboardReportService.createReport(dashboard.DashboardId);

        if (rawWidgets.length > 0)
        {
          currentReportId = rawWidgets[0]!.ReportId;
        }

        // For any Details widgets in the initial result, auto-apply default settings
        for (const w of rawWidgets)
        {
          if (w.Name === 'Details')
          {
            DashboardReportService.updateWidgetDetailsSettings({
              ReportId: w.ReportId,
              WidgetId: w.WidgetId,
              LayerId: 0,
              StartDate: new Date().toISOString().split('T')[0],
              DueDate: (() =>
              {
                const d = new Date();
                d.setDate(d.getDate() + 7);
                return d.toISOString().split('T')[0];
              })(),
            }).catch(() => {});
          }
        }

        if (currentReportId > 0)
        {
          onReportCreated?.(dashboard.DashboardId, currentReportId);
        }
      }

      setReportId(currentReportId);

      const kept = await processWidgets(rawWidgets, currentReportId);
      setWidgets(kept);
      setShowMenuItem(buildShowMenuItem(countByName(kept)));
      touchLastUpdate();
    }
    catch
    {
      setError('Failed to load report.');
    }
    finally
    {
      setLoading(false);
    }
  }, [dashboard.DashboardId, dashboard.ReportId, onReportCreated, processWidgets]);

  useEffect(() =>
  {
    initialize();
  }, [initialize]);

  const handleRefresh = async () =>
  {
    setShowMenu(false);
    try
    {
      setLoading(true);
      const raw = await DashboardReportService.getWidgets(reportId);
      const kept = await processWidgets(raw, reportId);
      setWidgets(kept);
      setShowMenuItem(buildShowMenuItem(countByName(kept)));
      touchLastUpdate();
    }
    catch
    {
      setError('Failed to refresh widgets.');
    }
    finally
    {
      setLoading(false);
    }
  };

  const handleAddWidget = async (widgetTypeId: number) =>
  {
    setShowMenu(false);
    try
    {
      const newWidget = await DashboardReportService.addWidget(reportId, widgetTypeId);
      const updated = [...widgets, newWidget];
      setWidgets(updated);
      setShowMenuItem(buildShowMenuItem(countByName(updated)));
    }
    catch
    {
      // ignore
    }
  };

  const handleRemoveWidget = useCallback((removed: ReportWidget) =>
  {
    setWidgets(prev =>
    {
      const updated = prev.filter(w => w.WidgetId !== removed.WidgetId);
      setShowMenuItem(buildShowMenuItem(countByName(updated)));
      return updated;
    });
  }, []);

  const handleUpdatedWidget = useCallback(
    (widgetId: number, newSettings: Record<string, unknown>) =>
    {
      setWidgets(prev =>
      {
        const index = prev.findIndex(x => x.WidgetId === widgetId);
        if (index < 0) return prev;

        const updated = [...prev];
        updated[index] = {
          ...updated[index]!,
          DataValuesJson: {
            ...updated[index]!.DataValuesJson,
            Settings: newSettings,
          },
        } as ReportWidget;
        return updated;
      });
    },
    [],
  );

  const handleDeleteReport = async () =>
  {
    setShowMenu(false);
    try
    {
      await DashboardReportService.deleteReport(reportId);
      setWidgets([]);
      onReportDeleted?.();
    }
    catch
    {
      // ignore
    }
  };

  const renderWidget = (item: ReportWidget) =>
  {
    const dashboardCtx = {
      DashboardId: dashboard.DashboardId,
      ReportId: reportId,
      Name: dashboard.Name,
      ConfigValueId: dashboard.ConfigValueId ?? 1,
      ProjectId: dashboard.ProjectId ?? 0,
    };

    const commonoProps = {
      widget: item,
      dashboard: dashboardCtx,
      filters: defaultFilters,
      onUpdateSettings: handleUpdatedWidget,
      onRemoveWidget: handleRemoveWidget,
    };

    switch (item.Name)
    {
      case 'Statistics':
        return <StatisticsWidget key={item.WidgetId} {...commonoProps} />;
      case 'Details':
        return <DetailsWidget key={item.WidgetId} {...commonoProps} layers={layers} />;
      case 'BurnDownChartFinished':
        return <BurndownChartWidget key={item.WidgetId} {...commonoProps} layers={layers} />;
      case 'KPIChart':
        return <KPIChartWidget key={item.WidgetId} {...commonoProps} />;
      case 'TrendChart':
        return <TrendChartWidget key={item.WidgetId} {...commonoProps} layers={layers} />;
      default:
        return null;
    }
  };

  const isDark = document.documentElement.classList.contains('dark');

  const modal = (
    <div
      className="fixed inset-0 z-[9999] ReportView"
      style={{ backgroundColor: 'rgba(0,0,0,0.45)' }}
    >
      <div
        className="bg-white dark:bg-[#333535] rounded-lg shadow-xl flex flex-col"
        style={{
          width: 750,
          maxHeight: '90vh',
          overflowY: 'auto',
          position: 'fixed',
          left: position.x,
          top: position.y,
        }}
      >
        {/* Modal Header — matches legacy layout */}
        <div className="sticky top-0 bg-white dark:bg-[#333535] z-10 border-b border-gray-200 dark:border-[#4a4c4c] px-5 pt-3 pb-2">
          {/* Row 1: "Report - Name" title — drag handle */}
          <div className="flex j">
            <h1
              className="react-corgee-report-header-title"
              onMouseDown={handleHeaderMouseDown}
              style={{ cursor: 'grab', userSelect: 'none' }}
            >
              Report - {dashboard.Name || 'Report'}
            </h1>
            {/* Close button */}
            <button
              onClick={onClose}
              aria-label="Close"
              style={{
                position: 'absolute',
                top: 14,
                right: 20,
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: isDark ? '#a9a093' : '#333',
                fontSize: 16,
                lineHeight: 1,
                zIndex: 20,
                fontWeight: 500,
                padding: '2px 4px',
              }}
            >
              ✕
            </button>
          </div>
          {/* Row 2: Last Update (left) | 3-dot menu (right) */}
          <div
            className="react-corgee-report-header-row"
            style={{ display: 'table', width: '100%', tableLayout: 'fixed', marginTop: 2 }}
          >
            {/* Last Update */}
            <div style={{ display: 'table-cell', verticalAlign: 'middle' }}>
              <div className="react-corgee-report-update-timestamp">
                {lastUpdate ? `Last Update: ${lastUpdate}` : ''}
              </div>
            </div>

            {/* 3-dot menu */}
            <div
              style={{
                display: 'table-cell',
                verticalAlign: 'middle',
                width: 48,
                textAlign: 'right',
              }}
            >
              <div className="relative inline-block" ref={menuRef}>
                <button
                  className="p-1 hover:bg-gray-100 dark:hover:bg-[#3a3c3c] rounded text-gray-500 dark:text-[#a9a093]"
                  onClick={() => setShowMenu(v => !v)}
                  aria-label="Report menu"
                >
                  <MoreVertical size={15} />
                </button>
                {showMenu && (
                  <div className="absolute right-0 top-7 z-50 bg-white dark:bg-[#333535] border border-gray-200 dark:border-[#4a4c4c] rounded shadow-lg min-w-[210px]">
                    <button
                      className="w-full text-left px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-[#3a3c3c] dark:text-[#a9a093]"
                      onClick={handleRefresh}
                    >
                      Refresh Report
                    </button>
                    {showMenuItem.BurnDownChartFinished && (
                      <button
                        className="w-full text-left px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-[#3a3c3c] dark:text-[#a9a093]"
                        onClick={() => handleAddWidget(WIDGET_TYPE.BurnDownChartFinished)}
                      >
                        Add New Burndown Chart
                      </button>
                    )}
                    {showMenuItem.Statistics && (
                      <button
                        className="w-full text-left px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-[#3a3c3c] dark:text-[#a9a093]"
                        onClick={() => handleAddWidget(WIDGET_TYPE.Statistics)}
                      >
                        Add New Card Statistics
                      </button>
                    )}
                    {showMenuItem.Details && (
                      <button
                        className="w-full text-left px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-[#3a3c3c] dark:text-[#a9a093]"
                        onClick={() => handleAddWidget(WIDGET_TYPE.Details)}
                      >
                        Add New Details
                      </button>
                    )}
                    {showMenuItem.KPIChart && (
                      <button
                        className="w-full text-left px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-[#3a3c3c] dark:text-[#a9a093]"
                        onClick={() => handleAddWidget(WIDGET_TYPE.KPIChart)}
                      >
                        Add New KPI Chart
                      </button>
                    )}
                    {showMenuItem.TrendChart && (
                      <button
                        className="w-full text-left px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-[#3a3c3c] dark:text-[#a9a093]"
                        onClick={() => handleAddWidget(WIDGET_TYPE.TrendChart)}
                      >
                        Add New Trend Chart
                      </button>
                    )}
                    <hr className="border-gray-100 dark:border-[#4a4c4c] my-1" />
                    <button
                      className="w-full text-left px-2 py-1 text-xs hover:bg-gray-50 dark:hover:bg-[#3a3c3c] text-red-600"
                      onClick={handleDeleteReport}
                    >
                      Delete Report
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Modal Body */}
        <div className="react-corgee-report flex-1">
          {loading && (
            <p className="report-widget-feedback-message text-sm text-center py-8">
              Loading report...
            </p>
          )}
          {error && !loading && <p className="text-sm text-red-500 text-center py-8">{error}</p>}
          {!loading && !error && widgets.length === 0 && (
            <p className="text-sm text-gray-400 dark:text-[#6e6860] text-center py-8 italic">
              No widgets. Use the menu to add widgets.
            </p>
          )}
          {!loading && widgets.map(renderWidget)}
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default ReportViewer;
